import type { GrowthSnapshot, GrowthSourceId } from './types';

export const PAID_CLICK_LABELS: Readonly<Record<string, string>> = {
  'growth.cvr': 'Click conversion',
};

/**
 * One word for one thing: the same wire id means a different measure on each source, so labels are set per source.
 * The namespaced ids are the Growth contract's names for the same metrics.
 */
const SOURCE_LABELS: Readonly<Partial<Record<GrowthSourceId, Readonly<Record<string, string>>>>> = {
  paid_media: {
    ...PAID_CLICK_LABELS,
    'growth.paid_media.cvr': 'Click conversion',
    'growth.sessions': 'Attributed sessions',
    'growth.paid_media.sessions': 'Attributed sessions',
  },
  gha: {
    ...PAID_CLICK_LABELS,
    'growth.sessions': 'Attributed sessions',
  },
  sessions: {
    'growth.cvr': 'Session conversion',
    'growth.sessions.order_rate': 'Session conversion',
  },
};

/** Spend rising or falling is neither good nor bad, so it never takes a direction colour, on any source. */
const CONTEXTUAL_IDS: ReadonlySet<string> = new Set(['growth.spend', 'growth.paid_media.cost']);

/** The split a breakdown label names: "Session CVR by channel" -> "channel". */
const BREAKDOWN_SPLIT = /^.* by /i;

/**
 * On paid media, growth.cvr is orders divided by paid clicks, not a session conversion rate. A breakdown's own
 * label ("Session CVR by channel") is built from the backend's metric label, so it takes the same name as the tiles.
 */
export function withGrowthMetricLabels(snapshot: GrowthSnapshot | undefined): GrowthSnapshot | undefined {
  if (!snapshot) return snapshot;
  const labels = SOURCE_LABELS[snapshot.source.id] ?? {};
  const needsLabel = snapshot.metrics.some((metric) => labels[metric.id]);
  const needsDirection = snapshot.metrics.some(
    (metric) => CONTEXTUAL_IDS.has(metric.id) && metric.favourableDirection !== 'contextual',
  );
  if (!needsLabel && !needsDirection) return snapshot;
  return {
    ...snapshot,
    metrics: snapshot.metrics.map((metric) => ({
      ...metric,
      ...(labels[metric.id] ? { label: labels[metric.id] } : {}),
      ...(CONTEXTUAL_IDS.has(metric.id) ? { favourableDirection: 'contextual' as const } : {}),
    })),
    breakdowns: snapshot.breakdowns.map((breakdown) =>
      labels[breakdown.metricId]
        ? { ...breakdown, label: breakdown.label.replace(BREAKDOWN_SPLIT, `${labels[breakdown.metricId]} by `) }
        : breakdown,
    ),
  };
}
